/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */
/** @typedef {import('./floorPlanDesignerDynamicWallEnds.js').DynamicWallRenderModel} DynamicWallRenderModel */

import {
  buildRoomBoundaryModel,
  getRoomBoundaryRefsFromModel,
} from './floorPlanDesignerRoomBoundaries.js';
import {
  buildDynamicWallRenderModel,
} from './floorPlanDesignerDynamicWallEnds.js';
import {
  buildPivotWallPolygon2D,
  toDesignerGraph,
} from './floorPlanDesignerPivotWallPolygons.js';

export { isPolygonSelfIntersecting, polygonArea } from './floorPlanDesignerWallRectangles.js';
export {
  buildPivotWallPolygonForSegment,
  buildPivotWallPolygon2D,
  designerGraphFromAnalysisJson,
  toDesignerGraph,
} from './floorPlanDesignerPivotWallPolygons.js';
export {
  buildDynamicWallRenderModel,
  buildSplitWallRenderModel,
  buildWallSegmentGeometry,
  validateDerivedPolygon,
} from './floorPlanDesignerDynamicWallEnds.js';
export {
  buildJunctionAwareWallGeometry,
  buildJunctionVerticesAtAllPivots,
  junctionWeldedEndKey,
  MAX_JOIN_DISTANCE_FACTOR,
} from './floorPlanDesignerJunctionAwareWallGeometry.js';

/**
 * @param {string} wallId
 * @param {DynamicWallRenderModel} renderModel
 * @returns {Point[][]}
 */
export function getWallCapDrawList(wallId, renderModel) {
  /** @type {Point[][]} */
  const list = [];
  const start = renderModel.startCaps.get(wallId);
  const end = renderModel.endCaps.get(wallId);
  if (start) list.push(...start);
  if (end) list.push(...end);
  return list;
}

/**
 * @param {string} wallId
 * @param {DynamicWallRenderModel} renderModel
 * @returns {Point[][]}
 */
export function getWallPolygonDrawListForWall(wallId, renderModel) {
  /** @type {Point[][]} */
  const list = [];
  const body = renderModel.wallBodies.get(wallId);
  if (body) list.push(body);
  const closurePolygons = renderModel.junctionClosurePolygonsByWall?.get(wallId);
  if (closurePolygons) list.push(...closurePolygons);
  return list;
}

/**
 * @param {Point[] | null | undefined} entry
 * @returns {Point[] | null}
 */
export function getPhysicalWallPolygon(entry) {
  if (!entry || !Array.isArray(entry) || entry.length === 0) return null;
  return entry;
}

/**
 * @param {Point[] | null | undefined} entry
 * @returns {Point[][]}
 */
export function getWallPolygonDrawList(entry) {
  const poly = getPhysicalWallPolygon(entry);
  return poly ? [poly] : [];
}

/**
 * @param {Point[] | null | undefined} entry
 * @returns {Point[] | null}
 */
export function getPrimaryWallPolygon(entry) {
  return getPhysicalWallPolygon(entry);
}

/**
 * @param {DesignerWall} wall
 * @param {DesignerState} state
 * @param {number} pixelsPerMeter
 * @param {DynamicWallRenderModel | null} [renderModel]
 * @returns {Point[] | null}
 */
export function buildWallPolygon2D(wall, state, pixelsPerMeter, renderModel = null) {
  const graph = toDesignerGraph(state);
  const model = renderModel ?? buildDynamicWallRenderModel(graph, pixelsPerMeter);
  return model.wallBodies.get(wall.id) ?? null;
}

/**
 * @param {DesignerState} state
 * @param {number} pixelsPerMeter
 * @returns {{ polygons: Map<string, Point[]>; renderModel: DynamicWallRenderModel }}
 */
export function buildAllWallRenderData2D(state, pixelsPerMeter) {
  const graph = toDesignerGraph(state);
  const renderModel = buildDynamicWallRenderModel(graph, pixelsPerMeter);
  return { polygons: renderModel.wallBodies, renderModel };
}

/**
 * @param {DesignerState | import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} input
 * @param {number} pixelsPerMeter
 * @returns {Map<string, Point[]>}
 */
export function buildAllWallPolygons2D(input, pixelsPerMeter) {
  const graph = toDesignerGraph(input);
  const renderModel = buildDynamicWallRenderModel(graph, pixelsPerMeter);
  return renderModel.wallBodies;
}

/**
 * @param {string} wallId
 * @param {Set<string>} partitionWallIds
 * @returns {boolean}
 */
export function isPartitionWall(wallId, partitionWallIds) {
  return partitionWallIds.has(wallId);
}

/**
 * @param {DesignerState | import('./floorPlanDesignerPivotWallPolygons.js').DesignerGraph} stateOrGraph
 * @returns {Array<{ boundaryWallId: string; wallId: string; roomId: string; directedEdgeKey: string; isMirror: boolean }>}
 */
export function getRoomBoundaryRefs(stateOrGraph) {
  const { rooms, roomBoundaryWalls } = buildRoomBoundaryModel(stateOrGraph);
  return getRoomBoundaryRefsFromModel(rooms, roomBoundaryWalls);
}

export { getBoundaryWallVisualGeometry } from './floorPlanDesignerRoomBoundaries.js';

/**
 * @param {Array<{ id: string; start: Point; end: Point; thicknessMeters?: number; startPointId?: string; endPointId?: string; color?: string }>} walls
 * @param {number} pixelsPerMeter
 * @param {{ points?: Array<{ id: string; x: number; y: number }>; wallSettings?: { thicknessMeters?: number; thicknessDirection?: 'inside' | 'outside' } } | null} [graphExtras]
 * @returns {Map<string, Point[]>}
 */
export function buildWallPolygonsFromWallList(walls, pixelsPerMeter, graphExtras = null) {
  if (graphExtras?.points?.length) {
    const graph = {
      points: graphExtras.points,
      walls: walls.map((wall) => ({
        id: wall.id,
        startPointId: wall.startPointId || '',
        endPointId: wall.endPointId || '',
        thicknessMeters: wall.thicknessMeters,
        color: wall.color || '#5f6b73',
      })),
      wallSettings: graphExtras.wallSettings || {
        thicknessMeters: 0.1,
        thicknessDirection: 'outside',
      },
    };
    return buildAllWallPolygons2D(graph, pixelsPerMeter);
  }

  /** @type {Map<string, Point[]>} */
  const map = new Map();
  for (const wall of walls) {
    if (!wall.start || !wall.end) continue;
    const pseudoGraph = {
      points: [
        { id: `${wall.id}_a`, x: wall.start[0], y: wall.start[1] },
        { id: `${wall.id}_b`, x: wall.end[0], y: wall.end[1] },
      ],
      walls: [
        {
          id: wall.id,
          startPointId: `${wall.id}_a`,
          endPointId: `${wall.id}_b`,
          thicknessMeters: wall.thicknessMeters,
          color: wall.color || '#5f6b73',
        },
      ],
      wallSettings: graphExtras?.wallSettings || {
        thicknessMeters: 0.1,
        thicknessDirection: 'outside',
      },
    };
    const poly = buildPivotWallPolygon2D(pseudoGraph.walls[0], pseudoGraph, pixelsPerMeter);
    if (poly && poly.length === 4) {
      map.set(wall.id, poly);
    }
  }
  return map;
}

/** @deprecated Centerline rectangles removed; kept for test compatibility */
export function buildSafeWallRectanglePolygon() {
  return null;
}
